/**
 * Chemical Inventory Report Export
 * Handles dual-quantity schema: unit containers (drums/bags/totes) + base measure (kg/gal/lbs)
 */

import { supabase } from '../../../../../supabaseClient';

const getReportStyles = () => `
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: 'Helvetica', 'Arial', sans-serif; padding: 20px; font-size: 10px; line-height: 1.4; }
  .header { text-align: center; margin-bottom: 20px; border-bottom: 2px solid #333; padding-bottom: 10px; }
  .company-name { font-size: 16px; font-weight: bold; color: #333; }
  .report-title { font-size: 14px; color: #666; margin-top: 2px; }
  .report-date { font-size: 8px; color: #999; margin-top: 2px; }
  .stats { display: flex; justify-content: space-around; margin: 15px 0; gap: 10px; }
  .stat-card { flex: 1; text-align: center; padding: 8px; border-radius: 4px; border: 1px solid #ddd; }
  .stat-card.total   { background: #e3f2fd; border-color: #1976d2; }
  .stat-card.in-stock { background: #e8f5e9; border-color: #388e3c; }
  .stat-card.low-stock { background: #fff3e0; border-color: #f57c00; }
  .stat-card.out-stock { background: #ffebee; border-color: #d32f2f; }
  .stat-number { font-size: 16px; font-weight: bold; margin-bottom: 2px; }
  .stat-number.total    { color: #1976d2; }
  .stat-number.in-stock { color: #388e3c; }
  .stat-number.low-stock{ color: #f57c00; }
  .stat-number.out-stock{ color: #d32f2f; }
  .stat-label { font-size: 8px; text-transform: uppercase; font-weight: bold; }
  table { width: 100%; border-collapse: collapse; font-size: 9px; margin-top: 10px; }
  th, td { padding: 6px 8px; text-align: left; border-bottom: 1px solid #ddd; vertical-align: top; }
  th { background-color: #f0f0f0; font-weight: bold; font-size: 8px; text-transform: uppercase; text-align: center; }
  tr:nth-child(even) { background-color: #f9f9f9; }
  .item-name { font-weight: bold; font-size: 9px; margin-bottom: 1px; }
  .item-details { font-size: 7px; color: #666; }
  .qty-main { font-weight: bold; }
  .qty-sub { font-size: 7.5px; color: #666; }
  .center-align { text-align: center; }
  .right-align { text-align: right; }
  .status { padding: 1px 3px; border-radius: 2px; font-size: 7px; font-weight: bold; text-transform: uppercase; text-align: center; display: inline-block; min-width: 50px; }
  .status.in-stock    { background: #d4edda; color: #155724; }
  .status.low-stock   { background: #fff3cd; color: #856404; }
  .status.out-of-stock { background: #f8d7da; color: #721c24; }
  .lot-row { background-color: #f5f5f5 !important; font-size: 8px; }
  .lot-indent { padding-left: 18px; font-size: 8px; color: #555; }
  .expiry-warn { color: #c62828; font-weight: bold; }
  .expiry-soon { color: #e65100; }
  @page { margin: 0; size: letter landscape; }
  @media print {
    body { margin: 0; padding: 10px; }
    tr { page-break-inside: avoid; }
    table { page-break-inside: auto; }
  }
`;

const getStockStatus = (item) => {
  const qty = item.total_quantity || 0;
  const rp  = item.reorder_point || 0;
  if (qty === 0)            return { label: 'Out of Stock', cls: 'out-of-stock' };
  if (rp > 0 && qty <= rp) return { label: 'Low Stock',    cls: 'low-stock' };
  return                          { label: 'In Stock',     cls: 'in-stock' };
};

const formatStock = (item) => {
  const unitQty  = Number(item.total_quantity       || 0);
  const baseQty  = Number(item.total_base_quantity  || 0);
  const unitType = item.unit_type    || 'units';
  const measure  = item.unit_measure || '';
  const unitStr  = `${unitQty.toLocaleString()} ${unitType}`;
  return measure
    ? `<span class="qty-main">${unitStr}</span><br/><span class="qty-sub">${baseQty.toLocaleString()} ${measure}</span>`
    : `<span class="qty-main">${unitStr}</span>`;
};

const formatLotQty = (lot, unitType, measure) => {
  const unitQty = Number(lot.current_unit_quantity  || 0);
  const baseQty = Number(lot.current_total_quantity || 0);
  const unitStr = `${unitQty} ${unitType || 'units'}`;
  return measure ? `${unitStr} / ${baseQty} ${measure}` : unitStr;
};

const formatExpiry = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  const now   = new Date();
  const days  = Math.ceil((date - now) / (1000 * 60 * 60 * 24));
  const label = date.toLocaleDateString();
  if (days < 0)   return `<span class="expiry-warn">${label} (Expired)</span>`;
  if (days <= 90) return `<span class="expiry-soon">${label} (${days}d)</span>`;
  return label;
};

const calcStats = (inventory) => {
  const totalItems  = inventory.length;
  const inStock     = inventory.filter(i => (i.total_quantity || 0) > 0).length;
  const lowStock    = inventory.filter(i => {
    const qty = i.total_quantity || 0;
    const rp  = i.reorder_point || 0;
    return rp > 0 && qty <= rp && qty > 0;
  }).length;
  const outOfStock  = inventory.filter(i => (i.total_quantity || 0) <= 0).length;
  return { totalItems, inStock, lowStock, outOfStock };
};

const statsBlock = (stats) => `
  <div class="stats">
    <div class="stat-card total">
      <div class="stat-number total">${stats.totalItems}</div>
      <div class="stat-label">Total Chemicals</div>
    </div>
    <div class="stat-card in-stock">
      <div class="stat-number in-stock">${stats.inStock}</div>
      <div class="stat-label">In Stock</div>
    </div>
    <div class="stat-card low-stock">
      <div class="stat-number low-stock">${stats.lowStock}</div>
      <div class="stat-label">Low Stock</div>
    </div>
    <div class="stat-card out-stock">
      <div class="stat-number out-stock">${stats.outOfStock}</div>
      <div class="stat-label">Out of Stock</div>
    </div>
  </div>
`;

const generateChemicalSummary = (companyName, inventory) => {
  const stats = calcStats(inventory);
  const rows = inventory.map(item => {
    const status    = getStockStatus(item);
    const stockHtml = formatStock(item);
    const rp        = item.reorder_point  ? `${item.reorder_point} ${item.unit_type || 'units'}` : '-';
    const rq        = item.reorder_quantity ? `${item.reorder_quantity} ${item.unit_type || 'units'}` : '-';

    return `
      <tr>
        <td>
          <div class="item-name">${item.item_name || 'Unknown'}</div>
          ${item.item_sku ? `<div class="item-details">SKU: ${item.item_sku}</div>` : ''}
        </td>
        <td class="center-align">${item.location_name || '-'}</td>
        <td class="center-align">${item.unit_type || '-'} / ${item.unit_measure || '-'}</td>
        <td class="center-align">${stockHtml}</td>
        <td class="center-align">${rp}</td>
        <td class="center-align">${rq}</td>
        <td class="center-align"><span class="status ${status.cls}">${status.label}</span></td>
      </tr>
    `;
  }).join('');

  return `<!DOCTYPE html><html>
    <head><title>Chemical Inventory Report</title><style>${getReportStyles()}</style></head>
    <body>
      <div class="header">
        <div class="company-name">${companyName}</div>
        <div class="report-title">Chemical Inventory Report — Summary</div>
        <div class="report-date">Generated: ${new Date().toLocaleString()}</div>
      </div>
      ${statsBlock(stats)}
      <table>
        <thead>
          <tr>
            <th width="22%">Chemical</th>
            <th width="12%">Location</th>
            <th width="12%">Unit / Measure</th>
            <th width="18%">Stock</th>
            <th width="12%">Reorder Point</th>
            <th width="12%">Reorder Qty</th>
            <th width="12%">Status</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
    </body>
  </html>`;
};

const generateChemicalDetailed = (companyName, inventory) => {
  const stats = calcStats(inventory);
  const rows = inventory.map(item => {
    const status    = getStockStatus(item);
    const stockHtml = formatStock(item);
    const unitType  = item.unit_type    || 'units';
    const measure   = item.unit_measure || '';
    const rp        = item.reorder_point ? `${item.reorder_point} ${unitType}` : '-';
    const activeLots = (item.lots || []).filter(l => l.is_active && (l.current_unit_quantity || 0) > 0);

    const headerRow = `
      <tr>
        <td>
          <div class="item-name">${item.item_name || 'Unknown'}</div>
          ${item.item_sku ? `<div class="item-details">SKU: ${item.item_sku}</div>` : ''}
        </td>
        <td class="center-align">${item.location_name || '-'}</td>
        <td class="center-align">${unitType} / ${measure || '-'}</td>
        <td class="center-align"><strong>${activeLots.length}</strong></td>
        <td class="center-align">${stockHtml}</td>
        <td class="center-align">${rp}</td>
        <td class="center-align"><span class="status ${status.cls}">${status.label}</span></td>
      </tr>
    `;

    const lotRows = activeLots.map(lot => `
      <tr class="lot-row">
        <td class="lot-indent">
          ${lot.lot_number   ? `Lot: ${lot.lot_number}`     : ''}
          ${lot.batch_number ? `&nbsp;&nbsp;Batch: ${lot.batch_number}` : ''}
        </td>
        <td></td>
        <td></td>
        <td class="center-align">1</td>
        <td class="center-align">${formatLotQty(lot, unitType, measure)}</td>
        <td class="center-align">${formatExpiry(lot.expiry_date)}</td>
        <td></td>
      </tr>
    `).join('');

    return headerRow + lotRows;
  }).join('');

  return `<!DOCTYPE html><html>
    <head><title>Chemical Inventory Report — Detailed</title><style>${getReportStyles()}</style></head>
    <body>
      <div class="header">
        <div class="company-name">${companyName}</div>
        <div class="report-title">Chemical Inventory Report — Detailed (Lot Breakdown)</div>
        <div class="report-date">Generated: ${new Date().toLocaleString()}</div>
      </div>
      ${statsBlock(stats)}
      <table>
        <thead>
          <tr>
            <th width="22%">Chemical</th>
            <th width="12%">Location</th>
            <th width="12%">Unit / Measure</th>
            <th width="8%">Lots</th>
            <th width="18%">Quantity</th>
            <th width="14%">Expiry Date</th>
            <th width="14%">Status</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
    </body>
  </html>`;
};

export const exportChemicalInventoryReport = async (companyId, inventory, reportType = 'summary') => {
  let companyName = 'Company';
  try {
    const { data } = await supabase.from('companies').select('name').eq('id', companyId).single();
    if (data) companyName = data.name;
  } catch (_) {}

  const html = reportType === 'detailed'
    ? generateChemicalDetailed(companyName, inventory)
    : generateChemicalSummary(companyName, inventory);

  const blob = new Blob([html], { type: 'text/html' });
  const url  = URL.createObjectURL(blob);

  const iframe = document.createElement('iframe');
  iframe.style.cssText = 'position:absolute;width:0;height:0;border:none;';
  iframe.src = url;
  document.body.appendChild(iframe);

  iframe.onload = () => {
    iframe.contentWindow.focus();
    iframe.contentWindow.print();
    setTimeout(() => {
      document.body.removeChild(iframe);
      URL.revokeObjectURL(url);
    }, 1000);
  };
};
